/*
 * atelier-hero-media.js — ATELIERFOOT 自研（T07-1 · 2026-10-24 · v2）
 *
 * 目的：商品页首屏把媒体画廊强制切到「痛点钩子图」（与 Facebook 广告素材同源），
 *       用户选择尺码 / Pack 后，交由 Dawn 原生 variant 逻辑切换到对应 SKU 图。
 *
 * 生效条件（任一不满足即静默退出，零副作用）：
 *   1. 页面存在 product-info 自定义元素；
 *   2. 该商品的媒体列表里存在目标 media（按 media id 匹配，见 HERO_MEDIA_ID）。
 *
 * 实现要点：
 *   - HERO_MEDIA_ID 是 MediaImage GID 的数字部分（如 49371194491190）。
 *     注意：它不是 REST products images 接口返回的 image id（那是另一套编号，
 *     混用会让本脚本静默失效）。换图时从商品媒体数据里拿新的 media id，改这一行即可。
 *   - setActiveMedia 由主题 assets/media-gallery.js 提供；找不到目标 media 时它内部
 *     会 fallback 到第一张图，所以调用前必须先确认目标 li 真实存在。
 *   - 只在页面加载时执行一次（data-hero-applied 标记防重复）；此后用户的一切选择
 *     都由 Dawn 原生逻辑处理，本脚本不再干预。
 *
 * v2 修订（2026-10-24）：
 *   v1 线上实测成功率仅 1/3：目标图位于 gallery 第 3 张（stacked 布局下在首屏折叠处），
 *   lazy→eager 改属性后浏览器能解码出图（naturalWidth>0）但 img.complete 长期为 false，
 *   导致 img.decode() 的 promise 永不 resolve，.then() 永不执行，卡在 data-hero-pending。
 *   v2 用 Promise.race 给 decode 加 1200ms 超时兜底，并对 setActiveMedia 做
 *   try/catch + 重试 + 结果校验（确认激活项确实是目标图，否则再试）。
 */
(function () {
  'use strict';

  // 痛点钩子图 01_pain_point.jpg 的 media id（未绑定任何 SKU）
  var HERO_MEDIA_ID = '49371194491190';
  var DECODE_TIMEOUT_MS = 1200;   // decode 兜底超时
  var SWITCH_MAX_TRIES = 4;       // setActiveMedia 重试次数
  var SWITCH_RETRY_MS = 200;      // 重试间隔

  function apply() {
    var info = document.querySelector('product-info');
    if (!info) return false;

    var sectionId = info.getAttribute('data-section');
    if (!sectionId) return false;

    var gallery = info.querySelector('media-gallery');
    if (!gallery || typeof gallery.setActiveMedia !== 'function') return false;
    if (gallery.getAttribute('data-hero-applied') === 'true') return true;
    if (gallery.getAttribute('data-hero-pending') === 'true') return true;

    var mediaId = sectionId + '-' + HERO_MEDIA_ID;
    var target = gallery.querySelector('[data-media-id="' + mediaId + '"]');
    if (!target) return false;

    gallery.setAttribute('data-hero-pending', 'true');

    // 首屏图预热：该图默认是 lazy（非首位 media），若直接切换会出现
    // 「已切入但图未加载」的空白窗口（实测 0.4~1.2s，弱网更长）。
    // 改 eager + 提高优先级；decode 完成或超时后再切换。
    var img = target.querySelector('img');
    var ready = Promise.resolve();
    if (img) {
      if (img.getAttribute('loading') === 'lazy') img.setAttribute('loading', 'eager');
      try { img.fetchPriority = 'high'; } catch (e) { /* 老浏览器无此属性，忽略 */ }
      if (!img.complete && typeof img.decode === 'function') {
        // 兜底：decode 可能因图在折叠/隐藏容器内而永不 resolve（v1 的坑），
        // 最多等 DECODE_TIMEOUT_MS，超时也照常切换（短暂过渡好过永远不切）。
        ready = Promise.race([
          img.decode().catch(function () { /* decode 失败也要照常切换 */ }),
          new Promise(function (resolve) { setTimeout(resolve, DECODE_TIMEOUT_MS); })
        ]);
      }
    }

    ready.then(function () {
      if (gallery.getAttribute('data-hero-applied') === 'true') return;
      var tries = 0;
      (function attempt() {
        tries += 1;
        var ok = false;
        try {
          gallery.setActiveMedia(mediaId, true);
          ok = true;
        } catch (e) { /* gallery 尚未初始化完，重试 */ }
        // 结果校验：激活项必须确实是目标图（setActiveMedia 找不到目标会 fallback 到第一张）
        var active = gallery.querySelector('[data-media-id].is-active, [data-media-id][aria-current="true"]');
        var activeId = active ? active.getAttribute('data-media-id') : null;
        if (!ok || (activeId && activeId !== mediaId)) {
          if (tries < SWITCH_MAX_TRIES) { setTimeout(attempt, SWITCH_RETRY_MS); return; }
        }
        gallery.setAttribute('data-hero-applied', 'true');
        gallery.removeAttribute('data-hero-pending');
      })();
    });
    return true;
  }

  function boot() {
    if (apply()) return;
    // 兜底：异步渲染 / 自定义元素升级未完成时，短窗口轮询（最多 300ms）
    var tries = 0;
    var timer = window.setInterval(function () {
      tries += 1;
      if (apply() || tries >= 6) window.clearInterval(timer);
    }, 50);
  }

  // 关键时序：本脚本位于 </body> 前（生产为 body 末尾 defer），执行时 media-gallery
  // 通常已解析完成 —— 先立即试一次，把目标图的 eager 提权提前约 500ms，
  // 压缩「duo 先绘制」的过渡窗口；失败（异步 section 场景）再走 DOMContentLoaded 兜底。
  if (apply()) {
    // 已立即生效
  } else if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
